import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useNiotextStore } from '@/store/modules/niotext/store.ts';
import { useContextStore } from '@/store/modules/context/store.ts';
import { useUserStore } from '@/store/modules/user/store.js';
import CommunicationMethod from '@/entities/CommunicationMethod.js';
import {
    leadCommunicatingApi,
    SetCommunicatingResultKind,
} from '@/entities/lead/leadCommunicatingApi.ts';
import type {
    CommunicatingLead,
    RecentCommunication,
} from '@/entities/lead/useLeadCommunicating.ts';

export function useSmsCommunicating() {
    const niotextStore = useNiotextStore();
    const contextStore = useContextStore();
    const userStore = useUserStore();
    const { selectedConversationId, selectedConversationKind, currentLeadId } =
        storeToRefs(niotextStore);

    // --- State ---
    const communicatingLead = ref<CommunicatingLead | null>(null);
    const recentCommunication = ref<RecentCommunication | null>(null);
    const isClaimed = ref(false);
    const isClaiming = ref(false);
    const claimedLeadId = ref<number | null>(null);

    // --- Computed ---
    const isConflict = computed(() => communicatingLead.value !== null);
    const isRecentCommunication = computed(
        () => recentCommunication.value !== null && !isConflict.value
    );
    const composerDisabled = computed(() => isConflict.value);

    // --- Internal ---
    function reset(): void {
        communicatingLead.value = null;
        recentCommunication.value = null;
        isClaimed.value = false;
        isClaiming.value = false;
        claimedLeadId.value = null;
    }

    const currentConversationIdentity = computed<string | null>(() => {
        const conversationId = selectedConversationId.value;
        const conversationKind = selectedConversationKind.value;
        const leadId = currentLeadId.value;

        if (!conversationId || !conversationKind || !leadId) {
            return null;
        }

        return `${conversationKind}:${conversationId}:${leadId}`;
    });

    // --- Actions ---
    async function check(): Promise<void> {
        const leadId = currentLeadId.value;
        const conversationIdentity = currentConversationIdentity.value;

        if (!leadId || !conversationIdentity) {
            return;
        }

        try {
            const data = await leadCommunicatingApi.checkCommunicating(leadId);

            if (currentConversationIdentity.value !== conversationIdentity) {
                return;
            }

            if (data.isCommunicating && data.lead) {
                contextStore.setCommunicating(data.lead);
                communicatingLead.value = data.lead;
                recentCommunication.value = null;
            } else {
                contextStore.removeCommunicating(leadId);
                communicatingLead.value = null;
            }
        } catch {
            // Silently fail - don't block the user
        }
    }

    async function claimOnWriteIntent(): Promise<void> {
        if (isClaimed.value || isClaiming.value || isConflict.value) {
            return;
        }

        const leadId = currentLeadId.value;
        const conversationIdentity = currentConversationIdentity.value;

        if (!leadId || !conversationIdentity) {
            return;
        }

        isClaiming.value = true;

        try {
            const result = await leadCommunicatingApi.setCommunicating(leadId, {
                type: CommunicationMethod.SMS,
            });
            const hasClaim =
                result.kind === SetCommunicatingResultKind.CLAIMED ||
                result.kind === SetCommunicatingResultKind.RECENT_COMMUNICATION;

            if (currentConversationIdentity.value !== conversationIdentity) {
                if (hasClaim) {
                    leadCommunicatingApi.removeCommunicating(leadId).catch(() => {});
                }
                return;
            }

            switch (result.kind) {
                case SetCommunicatingResultKind.CONFLICT:
                    contextStore.setCommunicating(result.communicatingLead);
                    communicatingLead.value = result.communicatingLead;
                    recentCommunication.value = null;
                    isClaimed.value = false;
                    claimedLeadId.value = null;
                    break;

                case SetCommunicatingResultKind.RECENT_COMMUNICATION:
                    communicatingLead.value = null;
                    recentCommunication.value = result.communication;
                    isClaimed.value = true;
                    claimedLeadId.value = leadId;
                    break;

                case SetCommunicatingResultKind.CLAIMED:
                    communicatingLead.value = null;
                    recentCommunication.value = null;
                    isClaimed.value = true;
                    claimedLeadId.value = leadId;
                    break;
            }
        } catch {
            // Silently fail - don't block the user
        } finally {
            if (currentConversationIdentity.value === conversationIdentity) {
                isClaiming.value = false;
            }
        }
    }

    async function release(): Promise<void> {
        const leadId = claimedLeadId.value;

        reset();

        if (!leadId) {
            return;
        }

        try {
            await leadCommunicatingApi.removeCommunicating(leadId);
        } catch {
            // Best effort release
        }
    }

    // --- Watchers ---

    // Watch selected conversation identity for conversation changes and drawer close
    watch(
        currentConversationIdentity,
        async () => {
            await release();
            await check();
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
        release();
    });

    // Watch broadcast events via context store communicatingLeads
    const broadcastCommunicatingLead = computed<CommunicatingLead | null>(() => {
        const leadId = currentLeadId.value;
        if (!leadId) {
            return null;
        }

        const authUser = userStore.authUser;

        const leads = contextStore.communicatingLeads as CommunicatingLead[];

        const match = leads.find(
            (l) => l.id === leadId && !authUser?.isSameUser(l.communicating_user?.id)
        );

        return match ?? null;
    });

    watch(
        broadcastCommunicatingLead,
        (newValue, oldValue) => {
            if (newValue) {
                communicatingLead.value = newValue;
                recentCommunication.value = null;
            } else if (oldValue) {
                communicatingLead.value = null;
                recentCommunication.value = null;
            }
        },
        { immediate: true }
    );

    return {
        communicatingLead,
        recentCommunication,
        isClaimed,
        isConflict,
        isRecentCommunication,
        composerDisabled,
        check,
        claimOnWriteIntent,
        release,
    };
}
